import { useCallback } from 'react'
import type { RefObject } from 'react'

import type { FragmentOf } from '~publish/graphql'

import type { InsertedRange } from '../../../components/EngagementInsertedTextHighlight'
import { useEngagementReplyForm } from '../../../components/EngagementReplyForm/hooks/useEngagementReplyForm'
import type { SavedReplyItem_SavedReply } from '../../../components/SavedRepliesPopover/SavedReplyItem'
import type { MentionData } from '../../context'
import type { EngagementReplyFormStatus } from '../../../components/EngagementReplyForm/hooks/types'
import { useCommunityConfig } from '../../../CommunityConfig'
import { useMentionData } from '../../context/MentionContext'
import { formatMentionUsername } from '../../../Comments/CommentReplyForm/utils/replyFormUtils'

import { useMentionReplySubmitStrategy } from './useMentionReplySubmitStrategy'

export type { InsertedRange }

/** Identical to the shared status machine; kept as a named export for consumers. */
export type MentionReplyFormStatus = EngagementReplyFormStatus

type UseMentionReplyFormProps = {
  channel: Parameters<typeof useEngagementReplyForm>[0]['channel']
  textareaRef: RefObject<HTMLTextAreaElement>
  expanded?: boolean
  /**
   * Fired once a reply publishes successfully. Wired to the list's
   * locally-resolved set (via `MentionCard`) so the replied card greys out.
   */
  onReplied?: () => void
  /**
   * Collapses this card. Wired to `MentionCard`'s `interaction.handleCollapse`
   * so an outside click collapses the expanded reply form.
   */
  onCollapse?: () => void
  /**
   * Collapses this card and opens the next repliable one. Fired on a short
   * delay after submit starts so the "Sending…" state is seen before the
   * transition. `onlyRepliable` skips notify-only / resolved cards.
   */
  onExpandNextMention?: (
    fromMentionId?: string,
    onlyRepliable?: boolean,
  ) => void
}

export type UseMentionReplyFormReturn = {
  // Form state
  text: string
  status: MentionReplyFormStatus
  error?: string
  submittingText?: string
  sentReplies: MentionData[]

  // Form actions
  setText: (text: string) => void
  focus: () => void
  clear: () => void
  submit: (e?: React.FormEvent) => Promise<void>

  // UI state
  discard: () => void
  insertSuggestion: (text: string) => boolean
  insertSavedReply: (
    savedReply: FragmentOf<typeof SavedReplyItem_SavedReply>,
  ) => void

  // Range of the freshly inserted AI suggestion or saved reply, which
  // `EngagementTextarea` paints with a rotating gradient until the animation
  // completes or the user edits. Null when nothing is highlighted.
  insertedRange: InsertedRange | null
  clearInsertedRange: () => void

  // Refs
  savedRepliesRef: React.RefObject<HTMLDivElement>
  emojiPickerRef: React.RefObject<HTMLDivElement>
}

/**
 * Manages mention reply form state and submission lifecycle.
 *
 * A thin wrapper over the shared `useEngagementReplyForm`: it supplies the
 * mention-specific submit strategy (`replyToEngagement` + a synthesized
 * optimistic reply, since the mutation returns only the parent), the
 * initial-text seed, the expand-next predicate, and the mention card's
 * click-outside selectors — then preserves the existing
 * `UseMentionReplyFormReturn` shape for its consumers.
 *
 * Submission drives an `idle → processing → published | error` machine. There
 * is no draft persistence or Pusher reconciliation: the engagement API returns
 * no reply to echo, so the sent reply is synthesized locally and tracked in
 * `sentReplies` until the next fetch.
 */
export const useMentionReplyForm = ({
  channel,
  textareaRef,
  expanded = false,
  onReplied,
  onCollapse,
  onExpandNextMention,
}: UseMentionReplyFormProps): UseMentionReplyFormReturn => {
  const { config } = useCommunityConfig()
  const mention = useMentionData()

  // The author handle to seed the reply with on channels whose replies need an
  // explicit @mention (Twitter, Mastodon, …). Empty on channels that don't
  // (`isMentionTypeTextSupported` false, e.g. Instagram). Channel settings load
  // async, so this is often empty at mount and re-seeds via `lateSeedText`.
  const seededReplyText =
    mention.author?.username && config?.isMentionTypeTextSupported(channel)
      ? `${formatMentionUsername(mention.author.username)} `
      : ''

  const onExpandNext = useCallback(() => {
    onExpandNextMention?.(mention.id, true)
  }, [onExpandNextMention, mention.id])

  const form = useEngagementReplyForm<MentionData>({
    channel,
    textareaRef,
    expanded,
    resetKey: mention.id,
    getInitialText: () => seededReplyText,
    lateSeedText: seededReplyText,
    onPublished: onReplied,
    // Skip auto-advance for already-resolved cards (re-replies).
    shouldExpandNext:
      mention.status !== 'replied' && mention.status !== 'dismissed',
    onExpandNext,
    clickOutside: {
      onCollapse,
      currentCardSelector: `[data-mention-id="${mention.id}"]`,
      cardWrapperSelector: '[data-mention-wrapper="true"]',
    },
    useSubmitStrategy: useMentionReplySubmitStrategy,
  })

  return form
}
